import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { CATEGORIES } from "./wbsConfig";
import { catKeyOf, estCost } from "./buildDashboardLines";
import { MEASURES } from "./DashboardKpis";
import { compactMoney, money } from "./format";
import DashboardSCurve from "./DashboardSCurve";
import DashboardCashFlow from "./DashboardCashFlow";

const LEGEND_BOTTOM = {
  direction: "row",
  position: { vertical: "bottom", horizontal: "middle" },
  padding: 0,
};

// Same four colors in every chart, so a category reads the same everywhere.
const useCategoryColors = () => {
  const { palette } = useTheme();
  return [
    palette.primary.main,
    palette.info.main,
    palette.warning.main,
    palette.success.main,
  ];
};

// One collapsible chart. Unmounted while closed: a chart measures its width
// when it mounts, so it must not be laid out while hidden.
function ChartAccordion({ title, subtitle, expanded, onChange, children }) {
  return (
    <Accordion
      variant="outlined"
      disableGutters
      expanded={expanded}
      onChange={onChange}
      TransitionProps={{ unmountOnExit: true }}
      sx={{ minWidth: 0, "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" component="div">
              {subtitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

const Empty = ({ children }) => (
  <Box sx={{ py: 8, textAlign: "center" }}>
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Box>
);

function EstimateMixChart({ totals, colors }) {
  const data = CATEGORIES.map((cat, i) => ({
    id: cat.k,
    value: totals.byCat[cat.k].est,
    label: `${cat.code} · ${cat.name}`,
    color: colors[i],
  })).filter((slice) => slice.value > 0);

  if (data.length === 0) return <Empty>Nothing estimated yet.</Empty>;

  return (
    <PieChart
      height={300}
      margin={{ top: 10, right: 10, bottom: 70, left: 10 }}
      series={[
        {
          data,
          innerRadius: 60,
          outerRadius: 105,
          paddingAngle: 2,
          cornerRadius: 3,
          valueFormatter: (item) => money(item.value),
        },
      ]}
      slotProps={{ legend: LEGEND_BOTTOM }}
    />
  );
}

// Each measure that has data as one bar, split by category - so it shows at a
// glance which categories the Actual / Invoiced / Paid figures actually cover.
function CostByMeasureChart({ totals, colors, measures }) {
  return (
    <BarChart
      height={300}
      margin={{ top: 10, right: 10, bottom: 70, left: 64 }}
      xAxis={[
        { scaleType: "band", data: measures.map((measure) => measure.label) },
      ]}
      yAxis={[{ valueFormatter: compactMoney }]}
      series={CATEGORIES.map((cat, i) => ({
        label: `${cat.code} · ${cat.name}`,
        stack: "category",
        color: colors[i],
        data: measures.map((measure) =>
          totals.byCat[cat.k].has[measure.key]
            ? totals.byCat[cat.k][measure.key]
            : 0,
        ),
        valueFormatter: (value) => (value ? money(value) : ""),
      }))}
      slotProps={{ legend: LEGEND_BOTTOM }}
    />
  );
}

// Fallback while only the Estimate exists: a one-bar comparison would say
// nothing, so show where the estimate is concentrated instead.
function EstimateByLineChart({ lines, colors }) {
  const ranked = lines
    .map((line) => ({ line, value: estCost(line) }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  if (ranked.length === 0) return <Empty>Nothing estimated yet.</Empty>;

  return (
    <BarChart
      layout="horizontal"
      height={Math.max(260, ranked.length * 30 + 100)}
      margin={{ top: 10, right: 20, bottom: 60, left: 170 }}
      yAxis={[
        {
          scaleType: "band",
          data: ranked.map(
            ({ line }) => `${line.displayCode} ${line.desc}`.slice(0, 30),
          ),
          tickLabelStyle: { fontSize: 11 },
        },
      ]}
      xAxis={[{ valueFormatter: compactMoney }]}
      series={CATEGORIES.map((cat, i) => ({
        label: `${cat.code} · ${cat.name}`,
        stack: "category",
        color: colors[i],
        data: ranked.map(({ line, value }) =>
          catKeyOf(line) === cat.k ? value : null,
        ),
        valueFormatter: (value) => (value ? money(value) : ""),
      }))}
      slotProps={{ legend: LEGEND_BOTTOM }}
    />
  );
}

const PANELS = ["mix", "detail", "scurve", "switch"];

/**
 * The dashboard's charts as four accordions (open by default), with one
 * button to collapse or expand them all - so the table can have the screen.
 *
 * @param curve         buildSCurve() result, for the S-curve panel
 * @param switchSchedule computeSwitchSchedule() / buildDemoSwitchSchedule() result
 * @param complianceFlags switchComplianceFlags() result
 * @param onEditPolicy  opens the cash-flow policy dialog
 * @param policyDisabled why editing is unavailable right now (falsy = enabled)
 * @param anyPaid  whether it's meaningful to say "nothing's been paid ahead of
 *                 its switch" - false while nothing real has been paid at all,
 *                 or while Paid is demo-substituted (see index.jsx)
 */
export default function DashboardCharts({
  lines,
  totals,
  curve,
  switchSchedule,
  complianceFlags,
  anyPaid,
  onEditPolicy,
  policyDisabled,
}) {
  const colors = useCategoryColors();
  const measures = MEASURES.filter(({ key }) => totals.has[key]);
  const [open, setOpen] = useState({ mix: true, detail: true, scurve: true, switch: true });

  const allOpen = PANELS.every((key) => open[key]);
  const setAll = (value) =>
    setOpen(Object.fromEntries(PANELS.map((key) => [key, value])));
  const onChange = (key) => (_event, isOpen) =>
    setOpen((prev) => ({ ...prev, [key]: isOpen }));

  const byMeasure = measures.length >= 2;

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" color="text.secondary">
          Charts
        </Typography>
        <Button size="small" onClick={() => setAll(!allOpen)}>
          {allOpen ? "Collapse all" : "Expand all"}
        </Button>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          alignItems: "start",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1fr) minmax(0, 1.4fr)",
          },
        }}
      >
        <ChartAccordion
          title="Estimate by category"
          subtitle={`Total ${money(totals.est)}`}
          expanded={open.mix}
          onChange={onChange("mix")}
        >
          <EstimateMixChart totals={totals} colors={colors} />
        </ChartAccordion>

        <ChartAccordion
          title={byMeasure ? "Cost by measure" : "Estimate by WBS line"}
          subtitle={
            byMeasure
              ? "Split by category — shows which categories each figure covers"
              : "Largest lines, by category"
          }
          expanded={open.detail}
          onChange={onChange("detail")}
        >
          {byMeasure ? (
            <CostByMeasureChart totals={totals} colors={colors} measures={measures} />
          ) : (
            <EstimateByLineChart lines={lines} colors={colors} />
          )}
        </ChartAccordion>
      </Box>

      <ChartAccordion
        title="Cost S-curve"
        subtitle="Cumulative by month · from dated POs, payments, MIS invoices and entered data"
        expanded={open.scurve}
        onChange={onChange("scurve")}
      >
        <DashboardSCurve
          curve={curve}
          estimateTotal={totals.has.est ? totals.est : null}
        />
      </ChartAccordion>

      {switchSchedule && (
        <ChartAccordion
          title="Cash flow — the switch"
          subtitle="Work done → client approves (switch fires, line billed) → client pays → vendor/payroll paid — deliberately in that order"
          expanded={open.switch}
          onChange={onChange("switch")}
        >
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Stack direction="row" justifyContent="flex-end">
              <Tooltip title={policyDisabled || ""}>
                <span>
                  <Button size="small" disabled={Boolean(policyDisabled)} onClick={onEditPolicy}>
                    Cash flow policy
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            {complianceFlags?.length > 0 ? (
              <Alert severity="error" variant="outlined">
                {complianceFlags.length} {complianceFlags.length === 1 ? "line was" : "lines were"}{" "}
                paid before any of its switches fired:{" "}
                {complianceFlags.map((f) => f.displayCode).join(", ")}.
              </Alert>
            ) : (
              anyPaid && (
                <Alert severity="success" variant="outlined">
                  Every paid line had its switch fire first — nothing has run
                  ahead of the policy.
                </Alert>
              )
            )}
            <DashboardCashFlow schedule={switchSchedule} flags={complianceFlags || []} />
          </Box>
        </ChartAccordion>
      )}
    </Box>
  );
}
